import { useState, useEffect, useCallback } from "react";
import { FileText, ChevronDown, Search, Plus, IdCard, Key, Calendar, Clock, CheckCircle2, Clock3, XCircle, User, Building2, Send, QrCode } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { WorkerForm, Item } from "../../../../types/workpermit";
import { GetLoadingDoor } from "../../../../services/dropdown";
import { DropdownClassData } from "../../../../services/workPermitFormService";
import WorkerSelectModal from "./WorkerSelectModal";

export interface ApprovalStep {
  WorkPermitApprovalId?: number;
  WorkPermitId?: number;
  ApprovalMasterId?: number;
  OrderNumber?: number;
  DepartmentId?: number;
  DepartmentName?: string;
  department?: string;
  WorkPermitApprovalStatusId?: number;
  WorkPermitApprovalStatusName?: string;
  status?: string;
  Remarks?: string | null;
  ModifiedOn?: string;
  ModifiedBy?: string;
  CreatedOn?: string;
  CreatedBy?: string;
  date?: string;
}

interface MaterialOutPermitDetailsProps {
  permitNumber: string;
  materialType: string;
  setMaterialType: (val: string) => void;
  loadingArea: string;
  setLoadingArea: (val: string) => void;
  workDescription?: string;
  setWorkDescription?: (val: string) => void;
  allWorkers: WorkerForm[];
  selectedWorkerIndices: number[];
  onAddWorker: (index: number) => void;
  onRemoveWorker: (index: number) => void;
  onEditWorker?: (index: number) => void;
  onShowWorkerQR?: (worker: WorkerForm) => void;
  employeeOptions?: DropdownClassData[];
  onSelectEmployee?: (emp: DropdownClassData) => void;
  handleViewKtp: (imgSrc: string) => void;
  materialSearchQuery: string;
  setMaterialSearchQuery: (val: string) => void;
  filteredMaterials: Item[];
  handleDeleteMaterial: (item: Item) => void;
  openEditMaterialModal: (material: Item) => void;
  openAddMaterialModal: (permitType: number) => void;
  options: { value: string; label: string; id: number }[];
  startDate: Date | null;
  endDate: Date | null;
  setDateRange: (update: [Date | null, Date | null]) => void;
  startTime: Date | null;
  setStartTime: (time: Date | null) => void;
  endTime: Date | null;
  setEndTime: (time: Date | null) => void;
  currentApprovalDepartment?: string;
  currentApprovalStatusName?: string;
  approvalDate?: string;
  approvalSteps?: ApprovalStep[];
  isReadOnly?: boolean;
  hasAnyApprovedByDept?: boolean;
  submissionDate?: string;
}

export default function MaterialOutPermitDetails({
  permitNumber,
  materialType,
  setMaterialType,
  loadingArea,
  setLoadingArea,
  workDescription = "",
  setWorkDescription,
  allWorkers,
  selectedWorkerIndices,
  onAddWorker,
  onRemoveWorker,
  onEditWorker,
  onShowWorkerQR,
  employeeOptions = [],
  onSelectEmployee,
  handleViewKtp,
  materialSearchQuery,
  setMaterialSearchQuery,
  filteredMaterials,
  handleDeleteMaterial,
  openEditMaterialModal,
  openAddMaterialModal,
  options,
  startDate,
  endDate,
  setDateRange,
  startTime,
  setStartTime,
  endTime,
  setEndTime,
  currentApprovalDepartment = "-",
  currentApprovalStatusName = "Draft",
  approvalDate = "-",
  approvalSteps = [],
  isReadOnly = false,
  hasAnyApprovedByDept = false,
  submissionDate = "-",
}: MaterialOutPermitDetailsProps) {
  const [showSelectModal, setShowSelectModal] = useState(false);
  const [durationHours, setDurationHours] = useState<"" | "12" | "24">("12");
  const isDisabled = isReadOnly || hasAnyApprovedByDept;

  // Auto-fill times when duration is selected
  const applyDuration = useCallback((hours: "12" | "24") => {
    const start = new Date();
    start.setHours(22, 0, 0, 0);
    setStartTime(start);
    const end = new Date();
    if (hours === "12") {
      end.setHours(10, 0, 0, 0);
    } else {
      end.setHours(22, 0, 0, 0);
    }
    setEndTime(end);
  }, [setStartTime, setEndTime]);

  const handleDurationChange = (val: "" | "12" | "24") => {
    setDurationHours(val);
    if (val === "12" || val === "24") {
      applyDuration(val);
    }
  };

  // Apply default 12h on first render (new form only)
  useEffect(() => {
    if (!isDisabled && !startTime && !endTime) {
      applyDuration("12");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const selectedWorkers = selectedWorkerIndices.map((i) => ({ worker: allWorkers[i], globalIndex: i }));
  const unselectedWorkers = allWorkers
    .map((w, i) => ({ worker: w, globalIndex: i }))
    .filter(({ globalIndex }) => !selectedWorkerIndices.includes(globalIndex));

  const selectedNames = new Set(selectedWorkers.map((w) => w.worker.PersonnelName.trim().toLowerCase()));

  const [loadingDoorOptions, setLoadingDoorOptions] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    GetLoadingDoor()
      .then((response) => {
        if (response.status === 200 && response.data?.dropdownClassDatas) {
          const doorOpts = response.data.dropdownClassDatas.map((item) => ({
            value: item.DataLabel,
            label: item.DataLabel,
          }));
          setLoadingDoorOptions(doorOpts);
          if (doorOpts.length === 1 && !loadingArea) {
            setLoadingArea(doorOpts[0].value);
          }
        }
      })
      .catch((err) => {
        console.error("Failed to load loading door options", err);
      });
  }, []);

  useEffect(() => {
    if (options.length === 1 && !materialType) {
      setMaterialType(options[0].value);
    }
  }, [options, materialType, setMaterialType]);

  useEffect(() => {
    if (loadingDoorOptions.length === 1 && !loadingArea) {
      setLoadingArea(loadingDoorOptions[0].value);
    }
  }, [loadingDoorOptions, loadingArea, setLoadingArea]);

  const isDraft = !approvalSteps || approvalSteps.length === 0;
  const statusText = isDraft ? "Draft" : (currentApprovalStatusName || "Pending");
  const statusLower = statusText.toLowerCase();

  const isApproved = statusLower === "approved" || statusLower === "1";
  const isRejected = statusLower.includes("reject");
  const isDraftStatus = statusLower === "draft";

  const getStepDepartment = (step: ApprovalStep) =>
    step.DepartmentName ||
    (step as any).departmentName ||
    (step as any).Department ||
    step.department ||
    (step as any).ApprovalMasterName ||
    (step as any).RoleName ||
    (step.DepartmentId ? `Dept ${step.DepartmentId}` : "-");
  const getStepStatus = (step: ApprovalStep) => step.WorkPermitApprovalStatusName || step.status || "Pending";
  const getStepDate = (step: ApprovalStep) => {
    const rawDate = step.ModifiedOn || step.date || step.CreatedOn;
    if (!rawDate || rawDate === "-") return "-";
    try {
      const d = new Date(rawDate);
      if (isNaN(d.getTime())) return rawDate;
      return d.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return rawDate;
    }
  };

  return (
    <div className="mb-8 bg-white dark:bg-gray-800 rounded-3xl border-2 border-slate-200 dark:border-gray-700 p-5 sm:p-6 shadow-sm transition-all">
      {/* Top Main Header Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b-2 border-slate-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-extrabold text-slate-900 dark:text-white">
            Detail Izin Keluar Barang
          </h2>
          <span
            className={`px-3 py-1 rounded-lg text-xs font-bold border transition-colors ${isDraftStatus
              ? "bg-slate-100 text-slate-600 border-slate-200 dark:bg-gray-700 dark:text-slate-300 dark:border-gray-600"
              : isApproved
                ? "bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800/40"
                : isRejected
                  ? "bg-rose-50 text-rose-600 border-rose-200 dark:bg-rose-950/40 dark:text-rose-400 dark:border-rose-800/40"
                  : "bg-amber-50 text-amber-600 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800/40"
              }`}
          >
            {statusText}
          </span>
        </div>

        <div className="flex items-center gap-4 text-xs">
          <div className="text-right">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Tanggal Pengajuan</p>
            <p className="font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1.5 justify-end">
              <Calendar className="w-3.5 h-3.5 text-blue-500" />
              <span>{submissionDate || "-"}</span>
            </p>
          </div>

          {!isDraft && (
            <>
              <div className="h-7 w-0.5 bg-slate-300 dark:bg-gray-600 hidden sm:block" />
              <div className="text-right hidden sm:block">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Last Approved By</p>
                <p className="font-extrabold text-slate-800 dark:text-white">{currentApprovalDepartment || "-"}</p>
              </div>

              <div className="h-7 w-0.5 bg-slate-300 dark:bg-gray-600 hidden sm:block" />

              <div className="text-right">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Tanggal Approval</p>
                <p className="font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1.5 justify-end">
                  <Calendar className="w-3.5 h-3.5 text-slate-400" />
                  <span>{approvalDate || "-"}</span>
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Main Body */}
      <div className="mt-6 space-y-6">
        {/* Top Form Fields */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div>
            <label className="block text-[11px] font-bold text-slate-400 dark:text-slate-500 mb-1.5 uppercase tracking-wide">
              Nomor Izin
            </label>
            <div className="relative">
              <FileText className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                value={permitNumber}
                disabled
                className="w-full pl-10 pr-4 py-2.5 text-xs bg-slate-50 text-slate-900 border border-slate-200 rounded-xl dark:bg-gray-750 dark:text-white dark:border-gray-600 cursor-not-allowed font-semibold"
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-bold text-slate-400 dark:text-slate-500 mb-1.5 uppercase tracking-wide">
              Kategori Pekerjaan
            </label>
            <div className="relative">
              <select
                value={materialType}
                disabled={isDisabled || options.length === 1}
                onChange={(e) => setMaterialType(e.target.value)}
                className="w-full px-4 py-2.5 text-xs bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 appearance-none transition-all font-semibold disabled:bg-slate-50 disabled:text-slate-900 dark:disabled:text-white disabled:cursor-not-allowed"
              >
                <option value="">Pilih Kategori</option>
                {options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none" />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-bold text-slate-400 dark:text-slate-500 mb-1.5 uppercase tracking-wide">
              Deskripsi
            </label>
            <input
              type="text"
              value={workDescription || ""}
              disabled={isDisabled}
              onChange={(e) => setWorkDescription?.(e.target.value)}
              placeholder="Deskripsi"
              className="w-full px-4 py-2.5 text-xs bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 transition-all font-medium disabled:bg-slate-50 disabled:text-slate-900 dark:disabled:text-white disabled:cursor-not-allowed"
            />
          </div>
        </div>

        {/* Middle Section: Jadwal Aktivitas & Approval Permit Status */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
          {/* Activity Schedule */}
          <div className={`${!isDraft ? "lg:col-span-2" : "lg:col-span-3"} h-full`}>
            <div className="h-full border border-slate-200/80 dark:border-gray-700 rounded-2xl p-4 bg-slate-50/50 dark:bg-gray-900/10 shadow-xs">
              <h3 className="text-xs font-extrabold text-slate-900 dark:text-white uppercase tracking-wider mb-4">
                Jadwal Aktivitas
              </h3>
              <div className="grid grid-cols-4 gap-3">
                <div className={!isDisabled ? "" : "col-span-2"}>
                  <label className="block text-[11px] font-bold text-slate-400 dark:text-slate-500 mb-1.5 uppercase tracking-wide">
                    Tanggal
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none z-10" />
                    <DatePicker
                      selectsRange
                      startDate={startDate}
                      endDate={endDate}
                      disabled={isDisabled}
                      onChange={(update) => setDateRange(update as [Date | null, Date | null])}
                      maxDate={startDate ? new Date(startDate.getFullYear(), startDate.getMonth() + 1, startDate.getDate()) : undefined}
                      dateFormat="dd MMM yy"
                      placeholderText="Pilih tanggal"
                      className="w-full pl-10 pr-2 py-2.5 text-xs bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 dark:bg-gray-750 dark:text-white dark:border-gray-600 transition-all font-semibold cursor-pointer disabled:bg-slate-50 disabled:text-slate-900 dark:disabled:text-white disabled:cursor-not-allowed"
                      wrapperClassName="w-full"
                      autoComplete="off"
                    />
                  </div>
                </div>

                {/* Lama Waktu Dropdown (hanya muncul jika draft / belum submit) */}
                {!isDisabled && (
                  <div>
                    <label className="block text-[11px] font-bold text-slate-400 dark:text-slate-500 mb-1.5 uppercase tracking-wide">
                      Lama Waktu
                    </label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none" />
                      <select
                        value={durationHours}
                        disabled={isDisabled}
                        onChange={(e) => handleDurationChange(e.target.value as "" | "12" | "24")}
                        className="w-full pl-9 pr-8 py-2.5 text-xs bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 appearance-none transition-all font-semibold disabled:bg-slate-50 disabled:text-slate-900 dark:disabled:text-white disabled:cursor-not-allowed"
                      >
                        <option value="">Pilih Durasi</option>
                        <option value="12">12 Jam</option>
                        <option value="24">24 Jam</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none" />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-[11px] font-bold text-slate-400 dark:text-slate-500 mb-1.5 uppercase tracking-wide">
                    Waktu Mulai
                  </label>
                  <div className="relative flex items-center border border-slate-200 dark:border-gray-600 rounded-xl px-3 py-2 bg-white dark:bg-gray-750 focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500 transition-all">
                    <Clock className="w-4 h-4 text-slate-400 mr-2 flex-shrink-0" />
                    <DatePicker
                      selected={startTime}
                      onChange={(date) => setStartTime(date)}
                      disabled={isDisabled}
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={10}
                      timeCaption="Start"
                      dateFormat="HH:mm"
                      className="w-full bg-transparent border-0 p-0 text-xs focus:ring-0 focus:outline-none dark:text-white font-semibold cursor-pointer disabled:text-slate-900 dark:disabled:text-white disabled:cursor-not-allowed"
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-400 dark:text-slate-500 mb-1.5 uppercase tracking-wide">
                    Waktu Selesai
                  </label>
                  <div className="relative flex items-center border border-slate-200 dark:border-gray-600 rounded-xl px-3 py-2 bg-white dark:bg-gray-750 focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500 transition-all">
                    <Clock className="w-4 h-4 text-slate-400 mr-2 flex-shrink-0" />
                    <DatePicker
                      selected={endTime}
                      onChange={(date) => setEndTime(date)}
                      disabled={isDisabled}
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={10}
                      timeCaption="End"
                      dateFormat="HH:mm"
                      className="w-full bg-transparent border-0 p-0 text-xs focus:ring-0 focus:outline-none dark:text-white font-semibold cursor-pointer disabled:text-slate-900 dark:disabled:text-white disabled:cursor-not-allowed"
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Approval Permit Status Box (Only shown when not draft) */}
          {!isDraft && (
            <div className="lg:col-span-1 h-full">
              <div className="h-full flex flex-col bg-slate-50/50 dark:bg-gray-900/30 rounded-2xl border border-slate-200/80 dark:border-gray-700/80 p-4 shadow-xs">
                <h3 className="text-xs font-extrabold text-slate-900 dark:text-white uppercase tracking-wider mb-3">
                  Approval Permit Status
                </h3>

                <div className="flex-1 bg-white dark:bg-gray-800 rounded-xl border border-slate-200/80 dark:border-gray-700 p-4">
                  <div className="space-y-0">
                    {/* First Step: Pengajuan Permit */}
                    <div className="relative flex items-start group">
                      {approvalSteps.length > 0 && (
                        <div className="absolute left-[13px] top-[26px] bottom-0 w-[2px] bg-slate-200 dark:bg-gray-700" />
                      )}

                      <div className="relative z-10 mr-3.5 flex-shrink-0 mt-0.5">
                        <div className="w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-xs">
                          <Send className="w-3.5 h-3.5 stroke-[2.5]" />
                        </div>
                      </div>

                      <div className="flex-1 pb-5 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-extrabold text-slate-900 dark:text-white truncate">
                            Pengajuan Permit
                          </span>
                          <span className="text-[11px] font-medium text-slate-400 dark:text-slate-500 flex-shrink-0">
                            {submissionDate || "-"}
                          </span>
                        </div>
                        <div className="mt-0.5">
                          <span className="text-[11px] font-semibold text-blue-600 dark:text-blue-400">
                            Submit
                          </span>
                        </div>
                      </div>
                    </div>

                    {approvalSteps.map((step, idx) => {
                      const stepDepartment = getStepDepartment(step);
                      const stepStatus = getStepStatus(step);
                      const stepDate = getStepDate(step);

                      const stLower = stepStatus.toLowerCase();
                      const stepApproved = stLower === "approved" || stLower === "1";
                      const stepRejected = stLower.includes("reject");

                      return (
                        <div key={idx} className="relative flex items-start group">
                          {idx < approvalSteps.length - 1 && (
                            <div className="absolute left-[13px] top-[26px] bottom-0 w-[2px] bg-slate-200 dark:bg-gray-700" />
                          )}

                          <div className="relative z-10 mr-3.5 flex-shrink-0 mt-0.5">
                            {stepApproved ? (
                              <div className="w-7 h-7 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-xs">
                                <CheckCircle2 className="w-4 h-4 stroke-[3]" />
                              </div>
                            ) : stepRejected ? (
                              <div className="w-7 h-7 rounded-full bg-rose-500 text-white flex items-center justify-center shadow-xs">
                                <XCircle className="w-4 h-4 stroke-[3]" />
                              </div>
                            ) : (
                              <div className="w-7 h-7 rounded-full border-2 border-amber-500 bg-white dark:bg-gray-800 text-amber-500 flex items-center justify-center shadow-xs">
                                <Clock3 className="w-4 h-4 stroke-[2.5]" />
                              </div>
                            )}
                          </div>

                          <div className="flex-1 pb-5 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-xs font-extrabold text-slate-900 dark:text-white leading-tight">
                                {stepDepartment}
                              </span>
                              <span className="text-[11px] font-medium text-slate-400 dark:text-slate-500 flex-shrink-0">
                                {stepDate}
                              </span>
                            </div>
                            <div className="mt-0.5">
                              <span
                                className={`text-[11px] font-semibold ${stepApproved
                                  ? "text-emerald-600 dark:text-emerald-400"
                                  : stepRejected
                                    ? "text-rose-600 dark:text-rose-400"
                                    : "text-amber-500 dark:text-amber-400"
                                  }`}
                              >
                                {stepStatus}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Bottom Section: Full Width Workers Table & Materials Table */}
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 max-w-2xl gap-5">
            {!isDisabled && (
              <div>
                <label className="block text-[11px] font-bold text-slate-400 dark:text-slate-500 mb-1.5 uppercase tracking-wide">
                  Tambah Pekerja
                </label>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none" />
                  <div
                    onClick={() => setShowSelectModal(true)}
                    className="w-full pl-10 pr-10 py-2.5 text-xs bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 cursor-pointer font-semibold flex items-center justify-between transition-all hover:border-blue-500 shadow-2xs"
                  >
                    <span className="text-slate-400 dark:text-slate-400">Pilih Pekerja...</span>
                    <ChevronDown className="w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>

                <WorkerSelectModal
                  isOpen={showSelectModal}
                  onClose={() => setShowSelectModal(false)}
                  employeeOptions={employeeOptions}
                  unselectedLocalWorkers={unselectedWorkers}
                  selectedWorkerNames={selectedNames}
                  onSelectEmployee={(emp) => {
                    if (onSelectEmployee) onSelectEmployee(emp);
                  }}
                  onSelectLocalWorker={(globalIndex) => {
                    onAddWorker(globalIndex);
                  }}
                />
              </div>
            )}

            <div>
              <label className="block text-[11px] font-bold text-slate-400 dark:text-slate-500 mb-1.5 uppercase tracking-wide">
                Area Loading
              </label>
              <div className="relative">
                <Building2 className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none" />
                <select
                  value={loadingArea}
                  disabled={isDisabled || loadingDoorOptions.length === 1}
                  onChange={(e) => setLoadingArea(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 text-xs bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 appearance-none transition-all font-semibold disabled:bg-slate-50 disabled:text-slate-900 dark:disabled:text-white disabled:cursor-not-allowed"
                >
                  <option value="">Pilih Area</option>
                  {loadingDoorOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Workers Table (Full Width) */}
          <div className="hidden md:block overflow-x-auto rounded-xl border border-slate-200/80 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xs">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-100 dark:border-gray-700 bg-slate-50/50 dark:bg-gray-800/40">
                  <th className="px-6 py-3 text-[10px] font-bold text-slate-400 dark:text-slate-400 uppercase tracking-widest w-16">No</th>
                  <th className="px-6 py-3 text-[10px] font-bold text-slate-400 dark:text-slate-400 uppercase tracking-widest">Nama</th>
                  <th className="px-6 py-3 text-[10px] font-bold text-slate-400 dark:text-slate-400 uppercase tracking-widest">No. KTP</th>
                  <th className="px-6 py-3 text-[10px] font-bold text-slate-400 dark:text-slate-400 uppercase tracking-widest">Dokumen</th>
                  <th className="px-6 py-3 text-[10px] font-bold text-slate-400 dark:text-slate-400 uppercase tracking-widest w-28 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
                {selectedWorkers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-xs text-slate-400 dark:text-slate-500 italic">
                      Tidak ada pekerja terpilih. Gunakan dropdown di atas untuk menambah pekerja.
                    </td>
                  </tr>
                ) : (
                  selectedWorkers.map(({ worker, globalIndex }, i) => (
                    <tr key={globalIndex} className="hover:bg-slate-50/50 dark:hover:bg-gray-700/10 transition-colors">
                      <td className="px-6 py-3.5 text-xs font-bold text-slate-800 dark:text-slate-200">{i + 1}</td>
                      <td className="px-6 py-3.5 text-xs font-bold text-slate-900 dark:text-white">{worker.PersonnelName}</td>
                      <td className="px-6 py-3.5 text-xs text-slate-600 dark:text-slate-400 font-mono">
                        <div className="flex items-center gap-1.5">
                          <span>{worker.IdentityNumber}</span>
                          {worker.IsAccessCard === 1 && <IdCard className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />}
                          {worker.IsParkingLotKey === 1 && <Key className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />}
                        </div>
                      </td>
                      <td className="px-6 py-3.5 text-xs">
                        {worker.DataImage && worker.DataImage.trim() !== "" ? (
                          <button
                            type="button"
                            onClick={() => handleViewKtp(worker.DataImage)}
                            className="px-3 py-1 border border-slate-200 dark:border-gray-700 text-[10px] font-extrabold text-slate-900 dark:text-white rounded-lg hover:bg-slate-50 dark:hover:bg-gray-700 transition shadow-xs"
                          >
                            Lihat KTP
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => onEditWorker && onEditWorker(globalIndex)}
                            className="px-3 py-1 bg-amber-50 text-[10px] font-extrabold text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800/60 rounded-lg hover:bg-amber-100 transition shadow-xs"
                          >
                            Upload KTP & Kartu Akses/Kunci
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-3.5 text-xs">
                        <div className="flex items-center justify-center gap-2">
                          {isApproved ? (
                            <button
                              type="button"
                              onClick={() => onShowWorkerQR && onShowWorkerQR(worker)}
                              className="px-3 py-1 bg-emerald-50 text-[10px] font-bold text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/40 rounded-lg hover:bg-emerald-100 transition shadow-xs inline-flex items-center gap-1"
                            >
                              <QrCode className="w-3.5 h-3.5" />
                              Show QR
                            </button>
                          ) : (
                            !isDisabled && (
                              <button
                                type="button"
                                onClick={() => onRemoveWorker(globalIndex)}
                                className="px-3 py-1 bg-red-50 text-[10px] font-bold text-red-600 rounded-lg hover:bg-red-100 transition"
                              >
                                Hapus
                              </button>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Material List Container (Full Width) */}
          <div className="border border-slate-200/80 dark:border-gray-700 rounded-2xl p-4 bg-[#F8FAFC]/60 dark:bg-gray-900/20">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-4">
              <div className="flex items-center gap-3 self-start sm:self-center w-full sm:w-auto">
                <h3 className="text-xs font-extrabold text-slate-900 dark:text-white uppercase tracking-wider flex-shrink-0">
                  Daftar Material
                </h3>
                <div className="relative w-full sm:w-48">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <input
                    type="text"
                    value={materialSearchQuery}
                    onChange={(e) => setMaterialSearchQuery(e.target.value)}
                    placeholder="Cari"
                    className="w-full pl-9 pr-4 py-1.5 text-xs bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 dark:bg-gray-750 dark:text-white dark:border-gray-600 transition-all shadow-sm"
                  />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                {!isDisabled && (
                  <button
                    type="button"
                    onClick={() => openAddMaterialModal(20)}
                    className="w-full sm:w-auto flex items-center justify-center gap-1.5 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl transition-all shadow-sm active:scale-98"
                  >
                    <Plus className="w-4 h-4" />
                    Tambah Material
                  </button>
                )}
              </div>
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto rounded-xl border border-slate-200/80 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xs">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-slate-100 dark:border-gray-700 bg-slate-50/50 dark:bg-gray-800/40">
                    <th className="px-6 py-3 text-[10px] font-bold text-slate-400 dark:text-slate-400 uppercase tracking-widest w-16">No</th>
                    <th className="px-6 py-3 text-[10px] font-bold text-slate-400 dark:text-slate-400 uppercase tracking-widest">Material</th>
                    <th className="px-6 py-3 text-[10px] font-bold text-slate-400 dark:text-slate-400 uppercase tracking-widest">Jumlah</th>
                    <th className="px-6 py-3 text-[10px] font-bold text-slate-400 dark:text-slate-400 uppercase tracking-widest">Satuan</th>
                    <th className="px-6 py-3 text-[10px] font-bold text-slate-400 dark:text-slate-400 uppercase tracking-widest w-36 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
                  {filteredMaterials.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center text-xs text-slate-400 dark:text-slate-500 italic">
                        Tidak ada material yang ditemukan.
                      </td>
                    </tr>
                  ) : (
                    filteredMaterials.map((item, index) => (
                      <tr key={index} className="hover:bg-slate-50/50 dark:hover:bg-gray-700/10 transition-colors">
                        <td className="px-6 py-3.5 text-xs font-bold text-slate-800 dark:text-slate-200">{index + 1}</td>
                        <td className="px-6 py-3.5 text-xs font-bold text-slate-900 dark:text-white">{item.Description}</td>
                        <td className="px-6 py-3.5 text-xs text-slate-800 dark:text-slate-200 font-semibold">{item.ItemQuantity}</td>
                        <td className="px-6 py-3.5 text-xs text-slate-900 dark:text-white font-bold">{item.ItemUnit}</td>
                        <td className="px-6 py-3.5 text-xs">
                          <div className="flex items-center justify-center gap-2">
                            {!isDisabled && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteMaterial(item)}
                                  className="px-3 py-1 bg-red-50 text-[10px] font-bold text-red-600 rounded-lg hover:bg-red-100 transition"
                                >
                                  Hapus
                                </button>
                                <button
                                  type="button"
                                  onClick={() => openEditMaterialModal(item)}
                                  className="px-3 py-1 border border-slate-200 text-[10px] font-bold text-slate-500 rounded-lg hover:bg-slate-50 transition"
                                >
                                  Edit
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}